import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { fmtDateTime } from '@/lib/format';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { ShieldCheck, RotateCcw, Plus, Trash2, AlertTriangle, Save } from 'lucide-react';

/**
 * Iter39 — Owner Settings: Visibility & Permissions matrices + Project
 * Exceptions + Audit trail with revert.
 *
 * Route: /admin/access (Owner-only; non-owners see a read-only warning).
 */
export default function AdminAccessPage() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [dirtyVis, setDirtyVis] = useState({}); // { module: { role: 'company_wide' } }
  const [dirtyPerm, setDirtyPerm] = useState({}); // { module: { role: { view:true } } }
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);

  const isOwner = user?.role === 'owner';

  const load = async () => {
    try {
      const [c, a] = await Promise.all([
        api.get('/admin/access/config'),
        api.get('/admin/access/audit'),
      ]);
      setCfg(c.data);
      setAudit(a.data || []);
    } catch (e) { toast.error(parseErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const setVis = (module, role, level) =>
    setDirtyVis(d => ({ ...d, [module]: { ...(d[module] || {}), [role]: level } }));
  const setPerm = (module, role, perm, on) =>
    setDirtyPerm(d => ({ ...d, [module]: { ...(d[module] || {}), [role]: { ...((d[module] || {})[role] || {}), [perm]: on } } }));

  const savedVis = (module, role) => dirtyVis[module]?.[role] ?? cfg?.visibility?.[module]?.[role];
  const savedPerm = (module, role, perm) => {
    if (dirtyPerm[module]?.[role]?.[perm] !== undefined) return dirtyPerm[module][role][perm];
    return !!cfg?.permission?.[module]?.[role]?.[perm];
  };

  const saveVis = async () => {
    if (!Object.keys(dirtyVis).length) return toast.info('No visibility changes to save.');
    setSaving(true);
    try {
      const r = await api.put('/admin/access/visibility', { matrix: dirtyVis });
      setCfg(r.data); setDirtyVis({});
      toast.success('Visibility matrix saved');
      const a = await api.get('/admin/access/audit'); setAudit(a.data || []);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };
  const savePerm = async () => {
    if (!Object.keys(dirtyPerm).length) return toast.info('No permission changes to save.');
    setSaving(true);
    try {
      const r = await api.put('/admin/access/permissions', { matrix: dirtyPerm });
      setCfg(r.data); setDirtyPerm({});
      toast.success('Permission matrix saved');
      const a = await api.get('/admin/access/audit'); setAudit(a.data || []);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };
  const revert = async (auditId) => {
    if (!window.confirm('Revert this settings change?')) return;
    try {
      await api.post(`/admin/access/audit/${auditId}/revert`);
      toast.success('Reverted');
      await load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  const openException = async () => {
    try {
      const [u, p] = await Promise.all([api.get('/users'), api.get('/master-projects')]);
      setUsers(u.data || []); setProjects((p.data || {}).rows || []);
      setExceptionOpen(true);
    } catch (e) { toast.error(parseErr(e)); }
  };

  const removeException = async (id) => {
    if (!window.confirm('Remove this exception?')) return;
    try { await api.delete(`/admin/access/exceptions/${id}`); toast.success('Removed'); await load(); }
    catch (e) { toast.error(parseErr(e)); }
  };

  if (!cfg) return <AppShell><div className="p-6">Loading Access settings…</div></AppShell>;

  return (
    <AppShell breadcrumbs={[{ label: 'Admin', to: '/admin/settings' }, { label: 'Access & Permissions' }]}>
      <div className="p-6 max-w-[1240px] mx-auto" data-testid="admin-access-page">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <ShieldCheck size={20} /> Access & Permissions
          </h1>
          {!isOwner && (
            <span className="chip chip-warning" data-testid="access-readonly-banner">
              <AlertTriangle size={11} className="mr-1" /> Read-only — only Owner can edit
            </span>
          )}
        </div>

        <Tabs defaultValue="visibility">
          <TabsList>
            <TabsTrigger value="visibility" data-testid="access-tab-visibility">Visibility</TabsTrigger>
            <TabsTrigger value="permissions" data-testid="access-tab-permissions">Permissions</TabsTrigger>
            <TabsTrigger value="exceptions" data-testid="access-tab-exceptions">Project Exceptions ({cfg.exceptions.length})</TabsTrigger>
            <TabsTrigger value="audit" data-testid="access-tab-audit">History</TabsTrigger>
          </TabsList>

          {/* ================= VISIBILITY ================= */}
          <TabsContent value="visibility">
            <div className="mt-3 border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              <table className="tbl w-full" data-testid="access-visibility-table">
                <thead>
                  <tr>
                    <th className="text-left">Module</th>
                    {cfg.roles.map(r => <th key={r.key} className="text-left">{r.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {cfg.modules.map(m => (
                    <tr key={m.key} data-testid={`access-vis-row-${m.key}`}>
                      <td className="font-medium">
                        {m.label}
                        {m.finance && <span className="chip chip-warning ml-2 text-[10px]">₹</span>}
                      </td>
                      {cfg.roles.map(r => (
                        <td key={r.key} data-testid={`access-vis-cell-${m.key}-${r.key}`}>
                          <select
                            className="border rounded-md h-8 text-[12.5px] px-1.5 bg-[hsl(var(--card))]"
                            style={{ borderColor: 'hsl(var(--border))' }}
                            value={savedVis(m.key, r.key) || 'no_access'}
                            disabled={!isOwner || r.key === 'owner'}
                            onChange={e => setVis(m.key, r.key, e.target.value)}>
                            {cfg.visibility_levels.map(lvl => (
                              <option key={lvl} value={lvl}>{lvl.replace(/_/g, ' ')}</option>
                            ))}
                          </select>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isOwner && (
              <div className="mt-3 flex justify-end">
                <Button onClick={saveVis} disabled={saving} data-testid="access-vis-save">
                  <Save size={13} className="mr-1" />
                  {saving ? 'Saving…' : `Save Visibility (${Object.keys(dirtyVis).length} module${Object.keys(dirtyVis).length === 1 ? '' : 's'})`}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ================= PERMISSIONS ================= */}
          <TabsContent value="permissions">
            <div className="mt-3 space-y-3">
              {cfg.modules.map(m => (
                <div key={m.key} className="border rounded-xl bg-[hsl(var(--card))] p-3"
                     style={{ borderColor: 'hsl(var(--border))' }}
                     data-testid={`access-perm-module-${m.key}`}>
                  <div className="font-medium mb-2 flex items-center gap-2">
                    {m.label}
                    {m.finance && <span className="chip chip-warning text-[10px]">₹</span>}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="tbl w-full">
                      <thead>
                        <tr>
                          <th className="text-left">Role</th>
                          {cfg.permissions.map(p => <th key={p} className="text-left">{p}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {cfg.roles.map(r => (
                          <tr key={r.key}>
                            <td className="text-[12.5px]">{r.label}</td>
                            {cfg.permissions.map(p => (
                              <td key={p} data-testid={`access-perm-cell-${m.key}-${r.key}-${p}`}>
                                <Checkbox
                                  checked={savedPerm(m.key, r.key, p)}
                                  disabled={!isOwner || r.key === 'owner'}
                                  onCheckedChange={v => setPerm(m.key, r.key, p, !!v)} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {isOwner && (
                <div className="flex justify-end">
                  <Button onClick={savePerm} disabled={saving} data-testid="access-perm-save">
                    <Save size={13} className="mr-1" />
                    {saving ? 'Saving…' : `Save Permissions (${Object.keys(dirtyPerm).length} module${Object.keys(dirtyPerm).length === 1 ? '' : 's'})`}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ================= EXCEPTIONS ================= */}
          <TabsContent value="exceptions">
            <div className="mt-3">
              <div className="flex justify-between mb-2">
                <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
                  Per-project grants. Finance ₹ stays hidden unless you tick <b>finance_visible</b>.
                </div>
                {isOwner && (
                  <Button variant="outline" onClick={openException} data-testid="access-exception-add">
                    <Plus size={12} className="mr-1" />New exception
                  </Button>
                )}
              </div>
              {cfg.exceptions.length === 0 ? (
                <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] p-4 text-center border rounded-md"
                     style={{ borderColor: 'hsl(var(--border))' }}
                     data-testid="access-exceptions-empty">
                  No project exceptions. Role rules apply to everyone.
                </div>
              ) : (
                <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
                     style={{ borderColor: 'hsl(var(--border))' }}>
                  <table className="tbl w-full" data-testid="access-exceptions-table">
                    <thead>
                      <tr>
                        <th>User</th><th>Project</th><th>Grant</th><th>Finance</th><th>Note</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cfg.exceptions.map(e => (
                        <tr key={e.id} data-testid={`access-exception-row-${e.id}`}>
                          <td>{e.user?.email || e.user_id}</td>
                          <td>{e.project?.project_name || e.project_id}</td>
                          <td><span className="chip">{e.grant}</span></td>
                          <td>{e.finance_visible ? '✅' : '—'}</td>
                          <td className="text-[12px] text-[hsl(var(--foreground-muted))] max-w-[220px] truncate">{e.note || '—'}</td>
                          <td>
                            {isOwner && (
                              <Button variant="ghost" size="sm" onClick={() => removeException(e.id)}
                                      data-testid={`access-exception-remove-${e.id}`}>
                                <Trash2 size={12} />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ================= AUDIT ================= */}
          <TabsContent value="audit">
            <div className="mt-3 border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              {audit.length === 0 ? (
                <div className="p-6 text-[12.5px] text-[hsl(var(--foreground-muted))] text-center"
                     data-testid="access-audit-empty">
                  No settings changes recorded yet.
                </div>
              ) : (
                <table className="tbl w-full" data-testid="access-audit-table">
                  <thead>
                    <tr><th>When</th><th>Kind</th><th>By</th><th></th></tr>
                  </thead>
                  <tbody>
                    {audit.map(a => (
                      <tr key={a.id} data-testid={`access-audit-row-${a.id}`}>
                        <td className="text-[12px]">{fmtDateTime(a.at)}</td>
                        <td><span className="chip">{a.kind_label}</span></td>
                        <td className="text-[12.5px]">{a.actor_email}</td>
                        <td>
                          {isOwner && (
                            <Button variant="ghost" size="sm" onClick={() => revert(a.id)}
                                    data-testid={`access-audit-revert-${a.id}`}>
                              <RotateCcw size={12} className="mr-1" />Revert
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Exception create dialog */}
        {exceptionOpen && (
          <ExceptionDialog
            users={users}
            projects={projects}
            onClose={() => setExceptionOpen(false)}
            onDone={async () => { setExceptionOpen(false); await load(); }} />
        )}
      </div>
    </AppShell>
  );
}


function ExceptionDialog({ users, projects, onClose, onDone }) {
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [grant, setGrant] = useState('viewer');
  const [finance, setFinance] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!userId || !projectId) return toast.error('Pick a user and a project');
    setBusy(true);
    try {
      await api.post('/admin/access/exceptions', {
        user_id: userId, project_id: projectId, grant,
        finance_visible: finance, note: note || null,
      });
      toast.success('Exception created');
      onDone?.();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const activeUsers = useMemo(() => users.filter(u => u.status !== 'inactive'), [users]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[480px]" data-testid="exception-dialog">
        <DialogHeader>
          <DialogTitle>New Project Access Exception</DialogTitle>
          <DialogDescription>
            Grant one user special access to a single project. Doesn&apos;t change their role.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[12px] text-[hsl(var(--foreground-muted))]">User</label>
            <select className="border rounded-md h-9 w-full text-[13px] px-2 bg-[hsl(var(--card))]"
                    style={{ borderColor: 'hsl(var(--border))' }}
                    value={userId} onChange={e => setUserId(e.target.value)}
                    data-testid="exception-user">
              <option value="">— Choose user —</option>
              {activeUsers.map(u => <option key={u.id} value={u.id}>{u.email} · {u.role}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] text-[hsl(var(--foreground-muted))]">Project</label>
            <select className="border rounded-md h-9 w-full text-[13px] px-2 bg-[hsl(var(--card))]"
                    style={{ borderColor: 'hsl(var(--border))' }}
                    value={projectId} onChange={e => setProjectId(e.target.value)}
                    data-testid="exception-project">
              <option value="">— Choose project —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.code || p.id.slice(0, 6)} · {p.project_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] text-[hsl(var(--foreground-muted))]">Grant</label>
            <select className="border rounded-md h-9 w-full text-[13px] px-2 bg-[hsl(var(--card))]"
                    style={{ borderColor: 'hsl(var(--border))' }}
                    value={grant} onChange={e => setGrant(e.target.value)}
                    data-testid="exception-grant">
              <option value="viewer">Viewer</option>
              <option value="contributor">Contributor</option>
              <option value="project_manager">Project Manager</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-[12.5px]">
            <Checkbox checked={finance} onCheckedChange={v => setFinance(!!v)}
                      data-testid="exception-finance" />
            Also allow this user to see ₹ financial data on this project
          </label>
          <div>
            <label className="text-[12px] text-[hsl(var(--foreground-muted))]">Note (optional)</label>
            <Input value={note} onChange={e => setNote(e.target.value)}
                   placeholder="Why the grant?"
                   data-testid="exception-note" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !userId || !projectId}
                  data-testid="exception-submit">
            {busy ? 'Saving…' : 'Grant access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
